import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, FileCheck2, Plus } from 'lucide-react';
import { Fragment, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ChipToggle,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Tabs,
  type Column,
  useToast,
} from '@/components/ui';
import {
  type PanelClaim,
  type PanelContract,
  useClaims,
  useFinanceActions,
  usePanels,
} from '@/features/admin-v2/api';
import {
  CLAIM_TABS,
  claimStatus,
  claimNumber,
  claimSla,
} from '@/features/admin-v2/claim-status';
import { useMe, useSession } from '@/features/auth/session-context';
import { formatRM } from '@/features/billing/money';
import { api, isApiError } from '@/lib/api';
import { useOptionalUrlState, useUrlState } from '@/lib/url-state';
import { dayLabel } from '@/pages/ops/register-format';
import { PanelContractLimits } from '@/pages/panel-limits/PanelContractLimits';
import styles from '@/pages/queue/queue.module.css';
import local from './finance.module.css';
import { PanelContractForm } from './PanelContractForm';

function useFail() {
  const toast = useToast();
  return (err: unknown) =>
    toast.show({
      tone: 'error',
      title: 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
}

const PANEL_KIND: Record<string, string> = {
  panel: 'TPA',
  insurer: 'Insurer',
  company: 'Corporate direct',
};
const PANEL_STATUS: Record<
  string,
  { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }
> = {
  live: { label: 'Active', tone: 'success' },
  // RL-1757 (gap #30): the API stores a new panel as "draft"; a rejected
  // draft is archived.
  draft: { label: 'Waiting for approval', tone: 'warning' },
  archived: { label: 'Not approved', tone: 'neutral' },
};
const PANEL_CODE = /^[a-z0-9_]{2,32}$/;
/** "PM Care Sdn Bhd" → "pm_care_sdn_bhd" (the API's code format). */
function panelCode(name: string) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32);
}
const METHOD: Record<string, string> = {
  portal: 'Portal',
  email: 'Email',
  export: 'Export file',
};

/** "Panels & contracts" (prototype panels(): clinic code per branch, windows, terms). */
export function Panels() {
  const me = useMe();
  const { hasPermission } = useSession();
  const panels = usePanels();
  const [drawer, setDrawer] = useOptionalUrlState('drawer');
  const adding = drawer === 'new';
  const viewing = panels.data?.find((panel) => panel.id === drawer) ?? null;
  const columns: Column<PanelContract>[] = [
    {
      key: 'name',
      header: 'Panel',
      render: (p) => (
        <>
          <p className={styles.name}>{p.name}</p>
          <p className={styles.sub}>{PANEL_KIND[p.kind] ?? p.kind}</p>
        </>
      ),
    },
    ...me.branches.map((b) => ({
      key: `code-${b.id}`,
      header: `Clinic code · ${b.name}`,
      render: (p: PanelContract) => (
        <span className="mono">{p.clinicCodes?.[b.code] || '-'}</span>
      ),
    })),
    {
      key: 'window',
      header: 'Submit within',
      render: (p) => (p.windowDays === null ? '-' : `${p.windowDays} days`),
    },
    {
      key: 'terms',
      header: 'Pays in',
      render: (p) => (p.termsDays === null ? '-' : `${p.termsDays} days`),
    },
    {
      key: 'method',
      header: 'Method',
      render: (p) =>
        p.submissionMethod
          ? (METHOD[p.submissionMethod] ?? p.submissionMethod)
          : '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) => {
        const s = PANEL_STATUS[p.status] ?? {
          label: p.status,
          tone: 'neutral' as const,
        };
        return (
          <Badge tone={s.tone} dot={s.tone === 'success'}>
            {s.label}
          </Badge>
        );
      },
    },
    {
      key: 'act',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (p) => (
        <Button size="sm" onClick={() => setDrawer(p.id)}>
          {p.status === 'draft' && hasPermission('panels:approve')
            ? 'Review'
            : 'View contract'}
        </Button>
      ),
    },
  ];
  return (
    <>
      <Card
        title="Panels & contracts"
        flush
        actions={
          hasPermission('panels:manage') && (
            <Button
              size="sm"
              variant="primary"
              icon={<Plus size={16} />}
              onClick={() => setDrawer('new')}
            >
              Add panel
            </Button>
          )
        }
      >
        <DataTable
          caption="Panels"
          columns={columns}
          rows={panels.data}
          rowKey={(p) => p.id}
          loading={panels.isLoading}
          error={panels.error}
          onRetry={() => void panels.refetch()}
          empty={<EmptyState title="No panels yet" />}
        />
      </Card>
      {adding && <AddPanelDrawer onClose={() => setDrawer(null)} />}
      {viewing && (
        <PanelContractDrawer panel={viewing} onClose={() => setDrawer(null)} />
      )}
    </>
  );
}

function usePanelMutations() {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: ['finance'] });
  return {
    draft: useMutation({
      mutationFn: (v: Record<string, unknown>) =>
        api.post('/finance/panels', v),
      onSuccess: done,
    }),
    decide: useMutation({
      mutationFn: (v: { id: string; approve: boolean; reason?: string }) =>
        api.post(`/finance/panels/${v.id}/decision`, {
          approve: v.approve,
          reason: v.reason,
        }),
      onSuccess: done,
    }),
  };
}

function AddPanelDrawer({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const fail = useFail();
  const { draft } = usePanelMutations();
  const [name, setName] = useState('');
  // RL-1756 (gap #29): the API needs a code; it follows the name until edited.
  const [code, setCode] = useState<string | null>(null);
  const shownCode = code ?? panelCode(name);
  const [kind, setKind] = useState<'panel' | 'insurer' | 'company'>('panel');
  const [windowDays, setWindow] = useState('');
  const [termsDays, setTerms] = useState('');
  const [exclusions, setExclusions] = useState('');
  const int = (v: string) => (v.trim() ? Number(v) : undefined);
  const submit = () => {
    const w = int(windowDays),
      t = int(termsDays);
    if (
      name.trim().length < 2 ||
      [w, t].some((n) => n !== undefined && (!Number.isInteger(n) || n < 1))
    )
      return toast.show({ tone: 'error', title: 'Check the panel details' });
    if (!PANEL_CODE.test(shownCode))
      return toast.show({
        tone: 'error',
        title: 'Check the panel code',
        body: '2–32 lowercase letters, numbers or _.',
      });
    draft
      .mutateAsync({
        name: name.trim(),
        code: shownCode,
        kind,
        windowDays: w,
        termsDays: t,
        exclusions: exclusions.trim() || undefined,
      })
      .then(() => {
        toast.show({
          tone: 'success',
          title: 'Panel drafted',
          body: 'A Super admin approves it before claims can use it.',
        });
        onClose();
      })
      .catch(fail);
  };
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title="Add panel"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={draft.isPending} onClick={submit}>
            Save draft
          </Button>
        </>
      }
    >
      <div className={local.form}>
        <p className={local.note}>
          A new panel waits for a Super admin's approval before claims can use
          it.
        </p>
        <Field label="Panel name">
          <Input
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field
          label="Panel code"
          hint="Short and unique, e.g. pmcare: lowercase letters, numbers or _."
        >
          <Input
            className="mono"
            value={shownCode}
            maxLength={32}
            onChange={(e) => setCode(e.target.value.toLowerCase())}
          />
        </Field>
        <ChipToggle
          label="Type"
          single
          options={[
            { value: 'panel', label: 'TPA' },
            { value: 'insurer', label: 'Insurer' },
            { value: 'company', label: 'Corporate direct' },
          ]}
          selected={[kind]}
          onChange={(v) => v[0] && setKind(v[0])}
        />
        <Field label="Submit within (days, optional)">
          <Input
            inputMode="numeric"
            value={windowDays}
            onChange={(e) => setWindow(e.target.value)}
          />
        </Field>
        <Field label="Pays in (days, optional)">
          <Input
            inputMode="numeric"
            value={termsDays}
            onChange={(e) => setTerms(e.target.value)}
          />
        </Field>
        <Field label="Not covered (optional)">
          <Input
            value={exclusions}
            maxLength={500}
            onChange={(e) => setExclusions(e.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

function PanelContractDrawer({
  panel,
  onClose,
}: {
  panel: PanelContract;
  onClose: () => void;
}) {
  const { hasPermission } = useSession();
  const toast = useToast();
  const fail = useFail();
  const me = useMe();
  const { decide } = usePanelMutations();
  const [reason, setReason] = useState('');
  const [editing, setEditing] = useState(false);
  const canApprove =
    panel.status === 'draft' && hasPermission('panels:approve');
  // RL-1758 (decision D21): Finance edits the contract terms.
  const canEdit = panel.status !== 'archived' && hasPermission('panels:manage');
  const go = (approve: boolean) => {
    if (!approve && !reason.trim())
      return toast.show({ tone: 'error', title: 'Give a reason to reject' });
    decide
      .mutateAsync({
        id: panel.id,
        approve,
        reason: reason.trim() || undefined,
      })
      .then(() => {
        toast.show({
          tone: 'success',
          title: approve ? 'Panel approved' : 'Panel rejected',
        });
        onClose();
      })
      .catch(fail);
  };
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={`Panel contract · ${panel.name}`}
      footer={
        editing ? undefined : canApprove ? (
          <>
            <Button
              variant="danger"
              loading={decide.isPending}
              onClick={() => go(false)}
            >
              Reject
            </Button>
            <Button
              variant="primary"
              loading={decide.isPending}
              onClick={() => go(true)}
            >
              Approve
            </Button>
          </>
        ) : canEdit ? (
          <Button variant="primary" onClick={() => setEditing(true)}>
            Edit contract
          </Button>
        ) : undefined
      }
    >
      {editing ? (
        <PanelContractForm panel={panel} onDone={() => setEditing(false)} />
      ) : (
        <div className={local.form}>
          {panel.status === 'draft' && (
            <p className={local.note}>
              Waiting for a Super admin's approval. The person who added it
              cannot approve it.
            </p>
          )}
          <dl className={local.kv}>
            <dt>Code</dt>
            <dd className="mono">{panel.code}</dd>
            <dt>Type</dt>
            <dd>{PANEL_KIND[panel.kind] ?? panel.kind}</dd>
            <dt>Submit within</dt>
            <dd>{panel.windowDays ?? '-'} days</dd>
            <dt>Pays in</dt>
            <dd>{panel.termsDays ?? '-'} days</dd>
            <dt>Monthly invoice</dt>
            <dd>
              {panel.invoiceDay === null
                ? 'None'
                : `${panel.invoiceDay === 31 ? 'Last day' : `Day ${panel.invoiceDay}`} · ${
                    panel.invoiceMode === 'auto'
                      ? 'sends automatically'
                      : 'review first'
                  } · reminder ${panel.reminderDaysBefore} days before`}
            </dd>
            <dt>Send claims by</dt>
            <dd>
              {panel.submissionMethod
                ? (METHOD[panel.submissionMethod] ?? panel.submissionMethod)
                : '-'}
            </dd>
            {me.branches.map((b) => (
              <Fragment key={b.id}>
                <dt>{`Clinic code · ${b.name}`}</dt>
                <dd className="mono">{panel.clinicCodes?.[b.code] || '-'}</dd>
              </Fragment>
            ))}
            <dt>Not covered</dt>
            <dd>{panel.exclusions || '-'}</dd>
            <dt>Billing email</dt>
            <dd>{panel.contactEmail || '-'}</dd>
            {panel.kind === 'company' && (
              <>
                <dt>Allowance</dt>
                <dd>
                  {panel.allowanceSen === null
                    ? '-'
                    : `${formatRM(panel.allowanceSen)} per employee a year`}
                </dd>
              </>
            )}
          </dl>
          {canApprove && (
            <Field label="Reason (needed to reject)">
              <Input
                value={reason}
                maxLength={300}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
          )}
          <PanelContractLimits
            panelId={panel.id}
            canManage={hasPermission('panels:manage')}
          />
        </div>
      )}
    </Dialog>
  );
}

type ClaimTab = keyof typeof CLAIM_TABS;
type ClaimAction =
  | 'fix'
  | 'reply'
  | 'query'
  | 'bill'
  | 'flag'
  | 'reject'
  | 'writeoff'
  | 'approve';
const CLAIM_ACTIONS: ClaimAction[] = [
  'fix',
  'reply',
  'query',
  'bill',
  'flag',
  'reject',
  'writeoff',
  'approve',
];
/** RL-1767: claims sent to the panel and still owed on (whole-claim write-off). */
const OWED = ['submitted', 'queried', 'part_paid', 'rejected'];
/** RL-1761: why the patient flagged the claim in the app. */
const FLAG_REASON: Record<string, string> = {
  wrong_details: 'The details are wrong',
  not_my_visit: 'Not my visit',
  amount_looks_wrong: 'The amount looks wrong',
  already_paid: 'Already paid',
  other: 'Something else',
};

/** "Claims" (prototype claims(): Open / Submitted / Closed tabs, Validate all, Create batch). */
export function Claims() {
  const { hasPermission } = useSession();
  const me = useMe();
  const [tab, setTab] = useUrlState<ClaimTab>(
    'tab',
    'open',
    Object.keys(CLAIM_TABS) as ClaimTab[],
  );
  const claims = useClaims('all');
  const actions = useFinanceActions();
  const toast = useToast();
  const fail = useFail();
  const [actionDrawer, setActionDrawer] = useOptionalUrlState('drawer');
  const canManage = hasPermission('claims:manage');
  // RL-1766: the clinic manager approves a whole-claim write-off.
  const canApproveWriteOff = hasPermission('claims:write_off_approve');
  const all = claims.data ?? [];
  const [actionName, actionClaimId] = actionDrawer?.split(':') ?? [];
  const actionClaim = all.find((claim) => claim.id === actionClaimId);
  const act =
    actionClaim && (CLAIM_ACTIONS as string[]).includes(actionName)
      ? { claim: actionClaim, action: actionName as ClaimAction }
      : null;
  const rows = all.filter((c) =>
    (CLAIM_TABS[tab] as readonly string[]).includes(c.status),
  );
  const count = (t: ClaimTab) =>
    all.filter((c) => (CLAIM_TABS[t] as readonly string[]).includes(c.status))
      .length;
  const columns: Column<PanelClaim>[] = [
    {
      key: 'claim',
      header: 'Claim',
      render: (c) => (
        <>
          <span className="mono">{claimNumber(c)}</span>
          <p className={styles.sub}>
            {c.bill.number} · {dayLabel(c.visitAt)}
          </p>
        </>
      ),
    },
    { key: 'patient', header: 'Patient', render: (c) => c.patient.fullName },
    {
      key: 'panel',
      header: 'Panel · branch',
      render: (c) => (
        <>
          {c.payer.name}
          <p className={styles.sub}>{c.branch.name}</p>
        </>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (c) => formatRM(c.amountSen),
    },
    {
      key: 'sla',
      header: 'SLA / expected',
      render: (c) => {
        const s = claimSla(c);
        return s ? (
          <span className={s.late ? local.late : local.ok}>{s.text}</span>
        ) : (
          '-'
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (c) => {
        const s = claimStatus(c.status);
        return (
          <span className={local.badges}>
            <Badge tone={s.tone}>{s.label}</Badge>
            {c.blockedReason && <Badge tone="danger">{c.blockedReason}</Badge>}
            {c.patientFlag?.status === 'open' && (
              <Badge tone="danger">Flagged by the patient</Badge>
            )}
            {c.writeOffRequest && (
              <Badge tone="warning">Write-off waiting for approval</Badge>
            )}
          </span>
        );
      },
    },
    {
      key: 'act',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (c) =>
        canApproveWriteOff &&
        c.writeOffRequest &&
        c.writeOffRequest.requestedById !== me.id ? (
          <Button
            size="sm"
            variant="primary"
            onClick={() => setActionDrawer(`approve:${c.id}`)}
          >
            Review write-off
          </Button>
        ) : canManage ? (
          <span className={local.acts}>
            {c.patientFlag?.status === 'open' && (
              <Button
                size="sm"
                variant="primary"
                onClick={() => setActionDrawer(`flag:${c.id}`)}
              >
                Patient flag
              </Button>
            )}
            {c.status === 'blocked' && (
              <Button
                size="sm"
                variant="primary"
                onClick={() => setActionDrawer(`fix:${c.id}`)}
              >
                Fix
              </Button>
            )}
            {c.status === 'queried' && (
              <Button
                size="sm"
                variant="primary"
                onClick={() => setActionDrawer(`reply:${c.id}`)}
              >
                Reply
              </Button>
            )}
            {c.status === 'submitted' && (
              <Button
                size="sm"
                onClick={() => setActionDrawer(`query:${c.id}`)}
              >
                Record query
              </Button>
            )}
            {['submitted', 'queried', 'part_paid'].includes(c.status) && (
              <Button
                size="sm"
                onClick={() => setActionDrawer(`reject:${c.id}`)}
              >
                Mark rejected
              </Button>
            )}
            {['blocked', 'queried', 'rejected'].includes(c.status) && (
              <Button size="sm" onClick={() => setActionDrawer(`bill:${c.id}`)}>
                Bill patient
              </Button>
            )}
            {OWED.includes(c.status) && !c.writeOffRequest && (
              <Button
                size="sm"
                onClick={() => setActionDrawer(`writeoff:${c.id}`)}
              >
                Write off claim
              </Button>
            )}
          </span>
        ) : null,
    },
  ];
  return (
    <>
      <Card
        title="Claims"
        flush
        actions={
          canManage && (
            <>
              <Button
                size="sm"
                icon={<BadgeCheck size={16} />}
                loading={actions.validateClaims.isPending}
                onClick={() =>
                  void actions.validateClaims
                    .mutateAsync()
                    .then(() =>
                      toast.show({
                        tone: 'success',
                        title: 'Claims validated',
                      }),
                    )
                    .catch(fail)
                }
              >
                Validate all
              </Button>
              <Button
                size="sm"
                variant="primary"
                icon={<FileCheck2 size={16} />}
                loading={actions.createBatch.isPending}
                onClick={() =>
                  void actions.createBatch
                    .mutateAsync()
                    .then(() =>
                      toast.show({ tone: 'success', title: 'Batch created' }),
                    )
                    .catch(fail)
                }
              >
                Create batch
              </Button>
            </>
          )
        }
      >
        <Tabs
          label="Claims"
          value={tab}
          onChange={setTab}
          items={[
            { value: 'open', label: 'Open', count: count('open') },
            { value: 'submitted', label: 'Submitted' },
            { value: 'closed', label: 'Closed' },
          ]}
        />
        <DataTable
          caption="Claims"
          columns={columns}
          rows={claims.data ? rows : undefined}
          rowKey={(c) => c.id}
          loading={claims.isLoading}
          error={claims.error}
          onRetry={() => void claims.refetch()}
          empty={<EmptyState title="No claims here" />}
        />
      </Card>
      {act && (
        <ClaimDrawer
          claim={act.claim}
          action={act.action}
          onClose={() => setActionDrawer(null)}
        />
      )}
    </>
  );
}

const TITLES: Record<ClaimAction, string> = {
  fix: 'Fix claim',
  reply: 'Reply to the panel',
  query: 'Record the panel query',
  bill: 'Bill the patient',
  flag: 'Patient flag',
  reject: 'Mark rejected',
  writeoff: 'Write off claim',
  approve: 'Approve the write-off',
};

function ClaimDrawer({
  claim: c,
  action,
  onClose,
}: {
  claim: PanelClaim;
  action: ClaimAction;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const [memberNo, setMemberNo] = useState(c.memberNo ?? '');
  const [glNumber, setGl] = useState(c.glNumber ?? '');
  // RL-1775: a diagnosis for the claim when the visit has none.
  const [dxCode, setDxCode] = useState('');
  const [dxText, setDxText] = useState('');
  const [text, setText] = useState('');
  const run = useMutation({
    mutationFn: (approve?: boolean) =>
      action === 'approve'
        ? api.post(`/finance/claims/${c.id}/write-off/decision`, { approve })
        : action === 'reject' || action === 'writeoff'
          ? api.post(
              `/finance/claims/${c.id}/${action === 'reject' ? 'reject' : 'write-off'}`,
              { reason: text.trim() },
            )
          : action === 'flag'
            ? api.post(`/finance/claims/${c.id}/patient-flag/resolve`)
            : action === 'fix'
              ? api.patch<PanelClaim>(`/finance/claims/${c.id}`, {
                  memberNo: memberNo.trim() || undefined,
                  glNumber: glNumber.trim() || undefined,
                  diagnosisCode: dxCode.trim() || undefined,
                  diagnosisText: dxText.trim() || undefined,
                })
              : api.post(
                  `/finance/claims/${c.id}/${action === 'bill' ? 'bill-patient' : action}`,
                  { text: text.trim() },
                ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['finance'] }),
  });
  const submit = () => {
    if (
      !['fix', 'flag', 'approve'].includes(action) &&
      text.trim().length <
        (action === 'reject' || action === 'writeoff' ? 3 : 2)
    )
      return toast.show({ tone: 'error', title: 'Write the text first' });
    if (action === 'fix' && !dxCode.trim() !== !dxText.trim())
      return toast.show({
        tone: 'error',
        title: 'Give both the diagnosis code and its description',
      });
    if (action === 'fix')
      return void run
        .mutateAsync(undefined)
        .then((r) => {
          const fixed = (r as { data?: PanelClaim }).data;
          toast.show(
            fixed?.status === 'ready'
              ? { tone: 'success', title: 'Claim is Ready' }
              : {
                  tone: 'info',
                  title: 'Saved, still blocked',
                  body: fixed?.blockedReason ?? undefined,
                },
          );
          onClose();
        })
        .catch(fail);
    decide();
  };
  const decide = (approve?: boolean) =>
    void run
      .mutateAsync(approve)
      .then(() => {
        toast.show({
          tone: 'success',
          title:
            action === 'approve'
              ? approve
                ? 'Claim written off'
                : 'Write-off declined'
              : action === 'writeoff'
                ? 'Sent to the clinic manager'
                : TITLES[action],
        });
        onClose();
      })
      .catch(fail);
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={TITLES[action]}
      footer={
        action === 'approve' ? (
          <>
            <Button
              variant="danger"
              loading={run.isPending}
              onClick={() => decide(false)}
            >
              Decline
            </Button>
            <Button
              variant="primary"
              loading={run.isPending}
              onClick={() => decide(true)}
            >
              Approve write-off
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={run.isPending} onClick={submit}>
              {action === 'fix'
                ? 'Save and revalidate'
                : action === 'flag'
                  ? 'Mark resolved'
                  : action === 'writeoff'
                    ? 'Ask for approval'
                    : 'Save'}
            </Button>
          </>
        )
      }
    >
      <div className={local.form}>
        <dl className={local.kv}>
          <dt>Claim</dt>
          <dd className="mono">{claimNumber(c)}</dd>
          <dt>Bill</dt>
          <dd className="mono">{c.bill.number}</dd>
          <dt>Patient</dt>
          <dd>{c.patient.fullName}</dd>
          <dt>Panel</dt>
          <dd>{c.payer.name}</dd>
          <dt>Amount</dt>
          <dd>{formatRM(c.amountSen)}</dd>
          {c.blockedReason && (
            <>
              <dt>Blocked</dt>
              <dd>{c.blockedReason}</dd>
            </>
          )}
          {c.queryText && (
            <>
              <dt>Panel asked</dt>
              <dd>{c.queryText}</dd>
            </>
          )}
        </dl>
        {action === 'approve' ? (
          <>
            <dl className={local.kv}>
              <dt>Write off</dt>
              <dd>
                {formatRM(c.amountSen - c.paidSen - c.writtenOffSen)} (all that
                is unpaid)
              </dd>
              <dt>Reason</dt>
              <dd>{c.writeOffRequest?.reason ?? '-'}</dd>
            </dl>
            <p className={local.note}>
              Approving closes the claim as written off. Finance cannot approve
              its own request.
            </p>
          </>
        ) : action === 'reject' || action === 'writeoff' ? (
          <>
            {action === 'writeoff' && (
              <p className={local.note}>
                The clinic manager approves writing off{' '}
                {formatRM(c.amountSen - c.paidSen - c.writtenOffSen)}.
              </p>
            )}
            <Field
              label={
                action === 'reject'
                  ? 'Why the panel rejected it (staff only)'
                  : 'Why it should be written off'
              }
            >
              <textarea
                className={local.textarea}
                value={text}
                maxLength={300}
                onChange={(e) => setText(e.target.value)}
              />
            </Field>
          </>
        ) : action === 'flag' ? (
          c.patientFlag && (
            <>
              <dl className={local.kv}>
                <dt>The patient says</dt>
                <dd>
                  {FLAG_REASON[c.patientFlag.reason] ?? c.patientFlag.reason}
                </dd>
                {c.patientFlag.note && (
                  <>
                    <dt>Note</dt>
                    <dd>{c.patientFlag.note}</dd>
                  </>
                )}
                <dt>Flagged</dt>
                <dd>{dayLabel(c.patientFlag.createdAt)}</dd>
              </dl>
              <p className={local.note}>
                Check the claim with the patient. Resolving closes the flag; it
                does not change the claim.
              </p>
            </>
          )
        ) : action === 'fix' ? (
          <>
            <Field label="Member number">
              <Input
                value={memberNo}
                maxLength={40}
                onChange={(e) => setMemberNo(e.target.value)}
              />
            </Field>
            <Field label="Guarantee letter number (optional)">
              <Input
                value={glNumber}
                maxLength={40}
                onChange={(e) => setGl(e.target.value)}
              />
            </Field>
            {!c.diagnosis && (
              <>
                <Field
                  label="Diagnosis code (ICD-10)"
                  hint="The visit has no diagnosis. This is kept on the claim only; the medical record does not change."
                >
                  <Input
                    className="mono"
                    value={dxCode}
                    maxLength={8}
                    onChange={(e) => setDxCode(e.target.value.toUpperCase())}
                  />
                </Field>
                <Field label="Diagnosis">
                  <Input
                    value={dxText}
                    maxLength={255}
                    onChange={(e) => setDxText(e.target.value)}
                  />
                </Field>
              </>
            )}
          </>
        ) : (
          <Field
            label={
              action === 'reply'
                ? 'Your reply'
                : action === 'query'
                  ? 'What the panel asked'
                  : 'Reason the patient pays (the patient sees this)'
            }
          >
            <textarea
              className={local.textarea}
              value={text}
              maxLength={1000}
              onChange={(e) => setText(e.target.value)}
            />
          </Field>
        )}
      </div>
    </Dialog>
  );
}
